import type { TuringMachine } from '../engine/types'

/**
 * Fixed worked serialization example for tree lookup on a Turing machine.
 *
 * The fixed demo tree ((a,b),(c,d)) is encoded as:
 *   @ * a b * c d # R L
 *
 * `@` marks the root of the current sub-expression.
 * `#` separates tree data from address symbols.
 * Address symbols are consumed left-to-right and rewritten as `X`.
 */
export const treeLookupTM: TuringMachine = {
  name: 'Tree Lookup (Tape)',
  description: 'Fixed worked serialization example: navigate a depth-2 tree encoded on tape',
  alphabet: ['@', '*', 'a', 'b', 'c', 'd', '#', 'R', 'L', 'X', '_'],
  states: [
    'read-addr',
    'seek-r',
    'seek-l',
    'skip-2',
    'skip-1',
    'arrive',
    'halt',
  ],
  initial: 'read-addr',
  halts: ['halt'],
  blank: '_',
  transitions: [
    // read-addr: find the next unconsumed address symbol.
    { state: 'read-addr', read: '@', write: '@', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: '*', write: '*', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: 'a', write: 'a', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: 'b', write: 'b', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: 'c', write: 'c', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: 'd', write: 'd', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: '#', write: '#', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: 'X', write: 'X', move: 'R', next: 'read-addr' },
    { state: 'read-addr', read: 'L', write: 'X', move: 'L', next: 'seek-l', milestone: 'address-consumed' },
    { state: 'read-addr', read: 'R', write: 'X', move: 'L', next: 'seek-r', milestone: 'address-consumed' },
    { state: 'read-addr', read: '_', write: '_', move: 'S', next: 'halt' },

    // seek-l: return to @, then enter the left sub-expression directly.
    { state: 'seek-l', read: '@', write: '*', move: 'R', next: 'arrive' },
    { state: 'seek-l', read: '*', write: '*', move: 'L', next: 'seek-l' },
    { state: 'seek-l', read: 'a', write: 'a', move: 'L', next: 'seek-l' },
    { state: 'seek-l', read: 'b', write: 'b', move: 'L', next: 'seek-l' },
    { state: 'seek-l', read: 'c', write: 'c', move: 'L', next: 'seek-l' },
    { state: 'seek-l', read: 'd', write: 'd', move: 'L', next: 'seek-l' },
    { state: 'seek-l', read: '#', write: '#', move: 'L', next: 'seek-l' },
    { state: 'seek-l', read: 'X', write: 'X', move: 'L', next: 'seek-l' },
    { state: 'seek-l', read: '_', write: '_', move: 'S', next: 'halt' },

    // seek-r: return to @, then skip the left sub-expression.
    { state: 'seek-r', read: '@', write: '*', move: 'R', next: 'skip-1' },
    { state: 'seek-r', read: '*', write: '*', move: 'L', next: 'seek-r' },
    { state: 'seek-r', read: 'a', write: 'a', move: 'L', next: 'seek-r' },
    { state: 'seek-r', read: 'b', write: 'b', move: 'L', next: 'seek-r' },
    { state: 'seek-r', read: 'c', write: 'c', move: 'L', next: 'seek-r' },
    { state: 'seek-r', read: 'd', write: 'd', move: 'L', next: 'seek-r' },
    { state: 'seek-r', read: '#', write: '#', move: 'L', next: 'seek-r' },
    { state: 'seek-r', read: 'X', write: 'X', move: 'L', next: 'seek-r' },
    { state: 'seek-r', read: '_', write: '_', move: 'S', next: 'halt' },

    // skip-1 / skip-2: skip one prefix sub-expression of the fixed tree.
    { state: 'skip-1', read: '*', write: '*', move: 'R', next: 'skip-2' },
    { state: 'skip-1', read: 'a', write: 'a', move: 'R', next: 'arrive' },
    { state: 'skip-1', read: 'b', write: 'b', move: 'R', next: 'arrive' },
    { state: 'skip-1', read: 'c', write: 'c', move: 'R', next: 'arrive' },
    { state: 'skip-1', read: 'd', write: 'd', move: 'R', next: 'arrive' },

    { state: 'skip-2', read: '*', write: '*', move: 'R', next: 'skip-2' },
    { state: 'skip-2', read: 'a', write: 'a', move: 'R', next: 'skip-1' },
    { state: 'skip-2', read: 'b', write: 'b', move: 'R', next: 'skip-1' },
    { state: 'skip-2', read: 'c', write: 'c', move: 'R', next: 'skip-1' },
    { state: 'skip-2', read: 'd', write: 'd', move: 'R', next: 'skip-1' },

    // arrive: mark internal node as current root or halt on leaf.
    { state: 'arrive', read: '*', write: '@', move: 'R', next: 'read-addr' },
    { state: 'arrive', read: 'a', write: 'a', move: 'S', next: 'halt' },
    { state: 'arrive', read: 'b', write: 'b', move: 'S', next: 'halt' },
    { state: 'arrive', read: 'c', write: 'c', move: 'S', next: 'halt' },
    { state: 'arrive', read: 'd', write: 'd', move: 'S', next: 'halt' },
  ],
}

/**
 * Create tape input for the fixed worked tree lookup example.
 * Tree: ((a,b),(c,d))
 * Address: sequence of R/L after a # separator.
 */
export function treeLookupTMInput(address: ('L' | 'R')[]): string[] {
  const tree = ['@', '*', 'a', 'b', '*', 'c', 'd']
  return [...tree, '#', ...address]
}
